import type { Node } from './../../../../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../../../../ricos-schema/legacy/wix/rich_content/v1';

import { LINK_TYPE } from '../../../consts';
import type { RangedDecoration } from '../../draft/toDraft/decorationParsers';
import { mergeTextNodes } from '../../draft/toDraft/decorationParsers';

type ToPlainTextGeneratorYield =
  | { command: 'getImageUrl'; data: string }
  | { command: 'getVideoUrl'; data: string }
  | { command: 'getVideoEmbeddedUrl'; data: string }
  | { command: 'getAudioUrl'; data: string }
  | { command: 'getGifUrl'; data: string };
export type ToPlainTextGenerator = Generator<ToPlainTextGeneratorYield, string, string>;

export const parseTextNodes = (node: Node, addTextLinks: boolean) => {
  const {
    text,
    decorationMap: { [LINK_TYPE]: linkDecorations },
  } = mergeTextNodes(node.nodes);
  const finalText = addTextLinks ? addLinksToText(text, linkDecorations) : text;
  return removeTrailingNewLine(finalText);
};

// Recursively flattens list and returns array of separate nodes rendered as strings
const listNodeToLines = (listNode: Node, addTextLinks: boolean, depth = 0): string[] =>
  listNode.nodes?.flatMap((listItemNode, listItemIndex) => {
    // List item can contain multiple paragraphs/lists. The first child is
    // always a paragraph and it gets the list symbol/number as prefix.
    return (
      listItemNode.nodes?.flatMap((listItemNodeChild, index) => {
        if (
          listItemNodeChild.type === Node_Type.PARAGRAPH ||
          listItemNodeChild.type === Node_Type.HEADING
        ) {
          const text = parseTextNodes(listItemNodeChild, addTextLinks);
          if (index === 0) {
            if (listNode.type === Node_Type.ORDERED_LIST) {
              return '   '.repeat(depth) + (listItemIndex + 1) + '. ' + text;
            }
            if (listNode.type === Node_Type.BULLETED_LIST) {
              return '   '.repeat(depth) + ' • ' + text;
            }
            // Illegal case - will fallback to no prefix
          }
          // paragraphs not in the beginning of the list item gets additional
          // indentation to align with the first paragraph
          return '   '.repeat(depth + 1) + text;
        }
        if (
          listItemNodeChild.type === Node_Type.ORDERED_LIST ||
          listItemNodeChild.type === Node_Type.BULLETED_LIST
        ) {
          return listNodeToLines(listItemNodeChild, addTextLinks, depth + 1);
        }

        // No other types of nodes should be in the list item
        return [];
      }) ?? []
    );
  }) ?? [];

// Takes ORDERED_LIST or BULLETED_LIST node and returns a string representation of it
export const parseListNode = (node: Node, delimiter: string, addTextLinks: boolean): string =>
  listNodeToLines(node, addTextLinks).join(delimiter);

const addLinksToText = (text: string, linkDecorations: RangedDecoration[] = []) =>
  linkDecorations
    .sort((a, b) => a.end - b.end)
    .reduce(
      (newText, link) =>
        insertInText(
          newText,
          link.end + newText.length - text.length + 1,
          `(${link.linkData?.link?.url})`
        ),
      text
    );

const insertInText = (text: string, pos: number, insertedText: string) =>
  text.substring(0, pos) + insertedText + text.substring(pos);

const removeTrailingNewLine = (text: string) =>
  text.endsWith('\n') ? text.substring(0, text.length - 1) : text;

export function* parseImage({ imageData }: Node, delimiter: string): ToPlainTextGenerator {
  const { caption } = imageData || {};
  const { src } = imageData?.image || {};
  const id = src?.id || src?.custom;

  const url = yield {
    command: 'getImageUrl',
    data: `https://static.wixstatic.com/media/${id?.replace('media/', '')}`,
  };
  return [caption, url].filter(Boolean).join(delimiter);
}

export const getDefaultVideoUrl = (fileId: string) => `https://video.wixstatic.com/${fileId}`;
export const getDefaultAudioUrl = (fileId: string) => `https://static.wixstatic.com/${fileId}`;

export function* parseVideo({ videoData }: Node, delimiter: string): ToPlainTextGenerator {
  const { video, title } = videoData || {};
  const { custom, id, url } = video?.src || {};
  const vidId = id || custom;
  const vidUrl = url || '';

  let videoUrl: string;
  if (vidId) {
    videoUrl = yield {
      command: 'getVideoUrl',
      data: vidId,
    } || '';
  } else {
    videoUrl = yield {
      command: 'getVideoEmbeddedUrl',
      data: vidUrl,
    } || vidUrl;
  }
  return title ? title + delimiter + videoUrl : videoUrl;
}

export function* parseAudio({ audioData }: Node, delimiter: string): ToPlainTextGenerator {
  const { audio, name } = audioData || {};
  const { id, url } = audio?.src || {};

  let audioUrl = url || '';
  if (id) {
    audioUrl = yield {
      command: 'getAudioUrl',
      data: id,
    } || '';
  }

  return name ? name + delimiter + audioUrl : audioUrl;
}

export function* parseGiphy({ gifData }: Node): ToPlainTextGenerator {
  let gifUrl = gifData?.original?.gif || '';
  if (gifUrl) {
    gifUrl = yield {
      command: 'getGifUrl',
      data: gifUrl,
    } || '';
  }

  return gifUrl;
}

export const parseAppEmbed = ({ appEmbedData }: Node, delimiter: string): string => {
  const { url, name } = appEmbedData || {};
  return [name, url].filter(Boolean).join(delimiter);
};

export const parseLinkPreview = ({ linkPreviewData }: Node): string => {
  const { url } = linkPreviewData?.link || {};
  return url || '';
};

export const parseEmbed = ({ embedData }: Node, delimiter?: string): string => {
  return [embedData?.oembed?.title, embedData?.src].filter(Boolean).join(delimiter);
};
